// Workspace top bar. On the left, where you are: on a hub's own pages
// (Projects, Agents, Automations: navModel) the hub's pages as tabs,
// elsewhere the breadcrumbs of a page under one (nothing on a top-level
// page: its h1 says it). On the right the jump palette trigger, the backend
// connection state (only when it is lost) and the notifications bell. Pages
// own their h1 — the bar never renders one.
//
// The hairline spans the column; the row inside takes the page's frame
// (features/navigation/pageFrame), so on a centred page the first crumb
// starts where the title starts and the bell ends where the content ends,
// at any width.
//
// In the Mac app (native/desktop.ts) the bar is half of the window's top
// row (MacHeader): it drags the window, and on the rail the sidebar
// toggle, Back and Forward open it.

import { useLocation } from "react-router-dom";

import { useHealth } from "@/api/health";
import { Badge } from "@/components/ui/Badge";
import { StatusDot } from "@/components/ui/StatusDot";
import { HubTabs } from "@/features/navigation/HubTabs";
import { JumpPalette } from "@/features/navigation/JumpPalette";
import { MacHeader } from "@/features/navigation/MacWindowControls";
import { hubTabsAt } from "@/features/navigation/navModel";
import { FRAME_ROW, hubFrameFor, pageFrameFor } from "@/features/navigation/pageFrame";
import { useNavFlags } from "@/features/navigation/useNavFlags";
import { NotificationsBell } from "@/features/notifications/NotificationsBell";
import { cn } from "@/lib/utils";
import { isMacShell } from "@/native/desktop";

import { Breadcrumbs } from "./Breadcrumbs";

interface CustomerHeaderProps {
  customerId: string;
}

export function CustomerHeader({ customerId }: CustomerHeaderProps) {
  const { pathname, search } = useLocation();
  // The same switches as the sidebar (`=== true`; remembered until the
  // settings answer): a tab of a page that is off never shows.
  const hub = hubTabsAt(pathname, search, useNavFlags(customerId));
  // A hub's tabs stay put as you switch between its pages.
  const frame = hub ? hubFrameFor(hub.row.pages.map((p) => p.to.split("?")[0])) : pageFrameFor(pathname);
  const row = (
    // px-8 = PageContainer's padding from md up (the desktop shell only
    // renders from 768px).
    <div className={cn("flex h-full w-full items-center gap-3 px-8", FRAME_ROW[frame])}>
      {hub ? (
        <HubTabs customerId={customerId} hub={hub} className="min-w-0 flex-1" />
      ) : (
        <Breadcrumbs customerId={customerId} className="min-w-0 flex-1" />
      )}
      <JumpPalette customerId={customerId} />
      <ConnectionStatus />
      <NotificationsBell customerId={customerId} />
    </div>
  );
  if (isMacShell()) return <MacHeader>{row}</MacHeader>;
  return <header className="h-header shrink-0 border-b border-line-subtle">{row}</header>;
}

// Whether the dashboard can reach its own server (/api/health), shown only
// when it cannot: nothing while healthy or on the first load, "Reconnecting…"
// while a failed check is retried, "Offline" once it failed again. A word
// plus a dot, never colour alone. The live region stays mounted (empty when
// healthy, its negative margin cancelling the row's gap) so a screen reader
// hears the change.
function ConnectionStatus() {
  const { isError, isFetching, isSuccess, errorUpdateCount } = useHealth();
  // A failed check that is retried with nothing read before goes back to
  // "pending" (no error) while it runs; it is still a lost connection.
  const lost = isError || (errorUpdateCount > 0 && !isSuccess);
  return (
    <span role="status" aria-live="polite" className="shrink-0 empty:-ml-3">
      {lost &&
        (isFetching ? (
          <Badge tone="default" icon={<StatusDot tone="neutral" size="sm" pulse />}>
            Reconnecting…
          </Badge>
        ) : (
          <Badge tone="danger" icon={<StatusDot tone="danger" size="sm" />} title="Can't reach the server. Trying again every 10 seconds.">
            Offline
          </Badge>
        ))}
    </span>
  );
}
